import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {TablesTranslations} from '~shared/components/tables/tables-translations.service';
import {PaginatedTableComponent} from '~shared/components/tables/paginated-table/paginated-table.component';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {SidebarService} from '~core/components/sidebar/sidebar.service';
import {AuthenticatedUser, Club, SidebarOptions, User} from '~core/models';
import {
  ClubService,
  DefaultNotificationService,
  NavigationService,
  PaginationService,
  StorageService,
  UserManagementService,
  UserService,
} from '~core/services';
import {ClubListTranslationsService, CustomerStatusTranslationsService, TooltipTranslationService} from '~shared/translations';

/**
 * Component responsible for club-overview administration.
 */
@UntilDestroy()
@Component({
  selector: 'app-club-overview-user-list',
  templateUrl: './club-user-list.component.html',
})
export class ClubUserListComponent implements OnInit, OnDestroy {
  @ViewChild(PaginatedTableComponent) paginatedTable: PaginatedTableComponent;
  public loading: boolean = true;
  public submitted: boolean = false;
  public listOfClubs: Array<Club>;

  public columns: Array<string> = [];
  public data: Array<User> = [];
  public keys: Array<Array<string>> = [];
  public baseApi: string;
  public customQueries: Array<any> = [];

  public authenticatedUser: AuthenticatedUser;
  public selectedClub: Club;
  public options: UntypedFormGroup;

  constructor(
    public notificationService: DefaultNotificationService,
    public sidebarService: SidebarService,
    public navigationService: NavigationService,
    public userService: UserService,
    public paginationService: PaginationService,
    public router: Router,
    public customerStatusTranslations: CustomerStatusTranslationsService,
    public clubService: ClubService,
    public clubListTranslations: ClubListTranslationsService,
    public storage: StorageService,
    public userModalService: UserManagementService,
    public route: ActivatedRoute,
    public tooltipTranslations: TooltipTranslationService,
    public flTranslations: FormLabelTranslationService,
    public formBuilder: UntypedFormBuilder,
    public tablesTranslations: TablesTranslations,
  ) {
    this.storage.castAuthenticatedUser.pipe(untilDestroyed(this))
      .subscribe((user: AuthenticatedUser) => user ? this.authenticatedUser = user : null);
    this.sidebarService.setSidebar(new SidebarOptions(false, false));
    this.navigationService.setCurrentPage('clubUserList');
    this.navigationService.setCurrentBreadcrumbs(['crm', 'clubUsers']);
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe(club => this.selectedClub = club);
  }

  /**
   * On init get the user id and load that user.
   */
  public ngOnInit(): void {
    this.getClubUsers(this.selectedClub.id);
    this.columns.push(
      this.tablesTranslations.i18nRole,
      this.tablesTranslations.i18nUsername,
      this.tablesTranslations.i18nFirstName,
      this.tablesTranslations.i18nLastName,
      this.tablesTranslations.i18nStatus,
    );
    this.keys.push(['type', 'username', 'firstName', 'lastName', 'status']);

    this.options = this.formBuilder.group({
      orSearchAll: ''
    });
  }

  /**
   * On component destroy, close the modal.
   */
  public ngOnDestroy(): void {
    this.userModalService.toggleModal(false);
  }

  /**
   * Get the club-overview users endpoing
   */
  public getClubUsers(id: number): any {
    const endpoint = this.userService.getClubUsersEndpoint(id);
    this.baseApi = endpoint[0];
    this.customQueries = endpoint[1];
    this.loading = false;
  }

  /**
   * Sets the new reminder list
   */
  public showUserList(users: Array<User>): void {
    this.data = users.map(user => new User(user, this.customerStatusTranslations));
  }

  /**
   * Triggers the search and updates the custom queries
   */
  public triggerSearch(): void {
    this.customQueries.push(this.options.value);
    this.paginatedTable.refetchData();
  }

  /**
   * Checks your status and moves you with router link
   */
  public routerLink(user: User): void {
    const path = '/manage/users';
    if ((this.authenticatedUser.isClubAdmin) && (user.type === 'CallCenterAgent')) {
      return;
    } else {
      this.router.navigate([path, user.id]);
    }
  }
}
